import { useState } from 'react';
import { Ship, Download, Anchor, Gauge, Ruler, Users, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Agent Boats Data
const agentBoats = [
  {
    id: 'rota',
    name: 'M/B ROTA',
    type: 'agent',
    image: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/o74mlaxj_temp_image_for_default_share.png',
    pdfUrl: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/bppbbgtc_Rota%20Particulars%200468.pdf',
    pdfName: 'Rota Particulars',
    trackingUrl: 'https://www.marinetraffic.com/en/ais/details/ships/shipid:337509/mmsi:271040446/imo:0/vessel:ROTA',
    specs: {
      'Length Overall': '10.00 m',
      'Breadth': '4.00 m',
      'Draft': '1.20 m',
      'Speed': '12 knots',
      'Capacity': '12 PAX',
      'Flag': 'Turkey',
    },
    description_en: 'High-speed agent boat for pilot transfer and ship agency operations in Turkish ports.',
    description_ru: 'Скоростной агентский катер для лоцманских операций в турецких портах.',
  },
  {
    id: 'levend',
    name: 'M/B LEVEND',
    type: 'agent',
    image: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/7rbjyq88_DJI_0020.JPG',
    pdfUrl: 'https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/cp9p67k0_LEVEND%20Ship%20Particular.pdf',
    pdfName: 'Levend Ship Particulars',
    trackingUrl: 'https://www.marinetraffic.com/en/ais/details/ships/shipid:8242129/mmsi:271049233/imo:0/vessel:LEVEND',
    specs: {
      'Length Overall': '14.95 m',
      'Breadth': '6.00 m',
      'Draft': '2.00 m',
      'Speed': '12 knots',
      'Capacity': '12 PAX',
      'Flag': 'Turkey',
    },
    description_en: 'Versatile agent boat serving the Black Sea ports for crew transfer and maritime operations.',
    description_ru: 'Универсальный агентский катер для портов Черного моря.',
  },
];

export const Fleets = () => {
  const { t, language } = useLanguage();
  const [selectedBoat, setSelectedBoat] = useState(null);

  return (
    <main data-testid="fleets-page" className="min-h-screen pt-24">
      {/* Header with Video Background */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/o74mlaxj_temp_image_for_default_share.png"
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src="https://customer-assets.emergentagent.com/job_seacaptain-log/artifacts/6xuvaprk_Rota_v1.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-navy/60 z-10" />
        
        <div className="container-bespoke relative z-20">
          <p className="text-editorial text-white/70 mb-6">
            {language === 'en' ? 'Our Fleet' : 'Наш флот'}
          </p>
          <h1
            data-testid="fleets-title"
            className="font-heading text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-8 drop-shadow-lg"
          >
            {language === 'en' ? 'Agent Boats' : 'Агентские катера'}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            {language === 'en' 
              ? 'Our fleet of modern agent boats ensures fast and efficient operations across all Turkish ports.'
              : 'Наш флот современных агентских катеров обеспечивает быстрые операции во всех турецких портах.'}
          </p>
        </div>
      </section>

      {/* Agent Boats Grid */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-bespoke">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {agentBoats.map((boat) => (
              <div
                key={boat.id}
                data-testid={`boat-card-${boat.id}`}
                className="group"
              >
                {/* Image */}
                <div 
                  className="relative overflow-hidden rounded-[20px] mb-8 cursor-pointer"
                  onClick={() => setSelectedBoat(boat)}
                >
                  <img
                    src={boat.image}
                    alt={boat.name}
                    className="w-full h-[400px] object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <span className="inline-block bg-signal text-white px-4 py-2 text-editorial mb-4">
                      {language === 'en' ? 'Agent Boat' : 'Агентский катер'}
                    </span>
                    <h3 className="font-heading text-3xl lg:text-4xl font-extrabold text-white">
                      {boat.name}
                    </h3>
                  </div>
                </div>

                {/* Info */}
                <div className="space-y-6">
                  <p className="text-slate-600 text-lg leading-relaxed">
                    {language === 'en' ? boat.description_en : boat.description_ru}
                  </p>

                  {/* Quick Specs */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-slate-50 p-4 rounded-lg text-center">
                      <Ruler className="w-5 h-5 text-signal mx-auto mb-2" />
                      <p className="font-mono text-navy font-bold">{boat.specs['Length Overall']}</p>
                      <p className="text-editorial text-slate-500 text-xs mt-1">Length</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg text-center">
                      <Gauge className="w-5 h-5 text-signal mx-auto mb-2" />
                      <p className="font-mono text-navy font-bold">{boat.specs['Speed']}</p>
                      <p className="text-editorial text-slate-500 text-xs mt-1">Speed</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg text-center">
                      <Users className="w-5 h-5 text-signal mx-auto mb-2" />
                      <p className="font-mono text-navy font-bold">{boat.specs['Capacity']}</p>
                      <p className="text-editorial text-slate-500 text-xs mt-1">Capacity</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg text-center">
                      <Anchor className="w-5 h-5 text-signal mx-auto mb-2" />
                      <p className="font-mono text-navy font-bold">{boat.specs['Draft']}</p>
                      <p className="text-editorial text-slate-500 text-xs mt-1">Draft</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-4">
                    <button
                      onClick={() => setSelectedBoat(boat)}
                      className="btn-outline"
                    >
                      {language === 'en' ? 'View Details' : 'Подробнее'}
                    </button>
                    <a
                      href={boat.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-ocean text-white uppercase tracking-[0.2em] text-[11px] font-bold px-6 py-4 hover:bg-ocean/90 transition-colors"
                    >
                      <MapPin className="w-4 h-4" />
                      {language === 'en' ? 'Track Live' : 'Отследить'}
                    </a>
                    <a
                      href={boat.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="inline-flex items-center gap-2 bg-signal text-white uppercase tracking-[0.2em] text-[11px] font-bold px-6 py-4 hover:bg-signal/90 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      {language === 'en' ? 'Download PDF' : 'Скачать PDF'}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Boat Detail Modal */}
      <Dialog open={!!selectedBoat} onOpenChange={() => setSelectedBoat(null)}>
        <DialogContent
          data-testid="boat-modal"
          className="bg-white text-navy max-w-3xl border-0"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-3xl text-navy">
              {selectedBoat?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedBoat && (
            <div className="space-y-6">
              <div className="relative h-72 overflow-hidden rounded-lg">
                <img
                  src={selectedBoat.image}
                  alt={selectedBoat.name}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-signal text-white px-4 py-2 text-editorial">
                    {language === 'en' ? 'Agent Boat' : 'Агентский катер'}
                  </span>
                </div>
              </div>

              <p className="text-slate-600 text-lg">
                {language === 'en' ? selectedBoat.description_en : selectedBoat.description_ru}
              </p>

              <div>
                <h4 className="font-heading font-bold text-navy mb-4">
                  {language === 'en' ? 'Specifications' : 'Характеристики'}
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {Object.entries(selectedBoat.specs).map(([key, value]) => (
                    <div key={key} className="bg-slate-50 p-4 rounded-lg">
                      <span className="text-editorial text-slate-500 block mb-1 text-sm">
                        {key}
                      </span>
                      <span className="font-mono text-navy font-bold text-lg">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <a
                  href={selectedBoat.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="inline-flex items-center gap-3 bg-signal text-white uppercase tracking-[0.2em] text-[11px] font-bold px-8 py-4 hover:bg-signal/90 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  {language === 'en' ? 'Download Ship Particulars (PDF)' : 'Скачать характеристики (PDF)'}
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};
